/**
 * 몬스터 SVG 스프라이트 로더 + 계열 판정.
 *
 * SVG 파일 위치: `web-client/public/assets/gfx/mobs/{family}_{direction}_{frame}.svg`
 *
 * 몬스터는 DB에만 1,800종이 넘어 개체마다 그림을 그릴 수 없다. 그래서 생김새를
 * **12개 계열(MobFamily)**로 접어 넣고 계열 그림 하나를 여럿이 나눠 쓴다
 * (12계열 × 8방향 × 5프레임 = 480장). 같은 계열끼리 구분이 안 되는 문제는
 * mobTint()가 이름 해시로 뽑은 색을 setTint()로 덧입혀 해결한다.
 *
 * 계열 판정은 두 단계다:
 *   1. `MOB_FAMILY_BY_GFX` — DB의 npcs 테이블에서 미리 뽑아둔 gfxId → 계열 표
 *   2. `MOB_FAMILY_KEYWORDS` — 표에 없는 gfxId가 오면 이름 키워드로 다시 판정
 * 두 규칙은 `scripts/gen_mob_families.py`가 함께 만들어내므로 서로 어긋나지 않는다.
 *
 * 텍스처 등록은 캐릭터 쪽과 같은 파이프라인(spriteLoader의 loadSvgAsTexture)을
 * 그대로 쓴다 — 캐시/중복요청 병합이 한 곳에 모여 있어야 하기 때문이다.
 */

import { loadSvgAsTexture, loadSpriteSheetTexture, DIRECTIONS } from './spriteLoader';
import type { Direction } from './spriteLoader';
import { MOB_FAMILY_BY_GFX, MOB_FAMILY_KEYWORDS } from '../data/mobFamilies';
import { mobImageMetaFor } from '../data/mobImages';
import type { MobImageMeta } from '../data/mobImages';

export type MobFamily =
  | 'humanoid' | 'undead' | 'beast' | 'insect' | 'reptile'
  | 'dragon' | 'elemental' | 'golem' | 'slime' | 'flying'
  | 'aquatic' | 'object';

export const MOB_FAMILIES: MobFamily[] = [
  'humanoid', 'undead', 'beast', 'insect', 'reptile',
  'dragon', 'elemental', 'golem', 'slime', 'flying',
  'aquatic', 'object',
];

/** 프레임 수 (0 = idle, 1~4 = 걷기). 캐릭터 스프라이트와 같은 규약이다. */
export const MOB_FRAME_COUNT = 5;

/** 판정에 실패했을 때의 계열. 사람형이 가장 무난하다. */
export const DEFAULT_MOB_FAMILY: MobFamily = 'humanoid';

// ============================================================
// 계열 판정
// ============================================================

/**
 * 이름 비교용 정규화 — 공백을 모두 지우고 소문자로 낮춘다.
 *
 * DB 이름의 띄어쓰기가 들쭉날쭉해서('셀로브' / '셸 로브', '드래곤 플라이' /
 * '드래곤플라이') 공백을 살려두면 같은 몹을 놓친다.
 * **`scripts/gen_mob_families.py`의 normalize()와 같은 규칙이어야 한다.**
 */
function normalizeMobName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '');
}

/**
 * 이름 키워드만으로 계열을 판정한다. 아무것도 못 맞추면 null.
 * 표는 위에서부터 훑어 **처음 걸린 규칙이 이긴다** (좁은 규칙이 위에 있다).
 */
function familyFromName(name: string): MobFamily | null {
  const target = normalizeMobName(name);
  if (!target) return null;

  for (const [keyword, family] of MOB_FAMILY_KEYWORDS) {
    if (target.includes(keyword)) return family;
  }
  return null;
}

/**
 * gfxId(+이름)로 몬스터 계열을 정한다.
 *
 * 1. DB에서 뽑아둔 gfxId 표
 * 2. 표에 없으면 이름 키워드
 * 3. 그래도 모르면 'humanoid'
 *
 * gfxId 표를 먼저 보는 이유는, 같은 gfxId를 쓰는 이름들이 다수결로 이미
 * 정리돼 있어 이름마다 흔들리지 않기 때문이다.
 */
export function inferMobFamily(gfxId: number, name?: string | null): MobFamily {
  const mapped = MOB_FAMILY_BY_GFX[gfxId];
  if (mapped) return mapped;

  if (name) {
    const inferred = familyFromName(name);
    if (inferred) return inferred;
  }
  return DEFAULT_MOB_FAMILY;
}

// ============================================================
// 개체 색 (이름 해시 → tint)
// ============================================================

/**
 * 32비트 해시 (FNV-1a + 마무리 확산).
 *
 * 확산을 붙이는 이유: FNV-1a는 짧은 문자열에서 하위 비트가 잘 안 섞여
 * '오크 궁수' / '오크 전사'처럼 한 글자만 다른 이름이 비슷한 색을 받는다.
 */
function hash32(text: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491) >>> 0;
  h ^= h >>> 13;
  return h >>> 0;
}

/** HSL(0~1 채도/명도) → 0xRRGGBB. */
function hslToRgb(h: number, s: number, l: number): number {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;

  if (hp < 1) { r = c; g = x; } else if (hp < 2) { r = x; g = c; } else if (hp < 3) { g = c; b = x; } else if (hp < 4) { g = x; b = c; } else if (hp < 5) { r = x; b = c; } else { r = c; b = x; }

  const m = l - c / 2;
  const to255 = (v: number) => Math.max(0, Math.min(255, Math.round((v + m) * 255)));
  return (to255(r) << 16) | (to255(g) << 8) | to255(b);
}

/**
 * 몬스터 이름 → 개체 색(0xRRGGBB). 같은 이름이면 언제나 같은 색인 순수 함수다.
 *
 * 계열 SVG는 tint가 곱해지도록 밝은 회색빛으로 그려져 있다. 그래서 어둡거나
 * 채도가 낮은 색이 나오면 스프라이트가 통째로 뭉개져 형체를 알아볼 수 없다.
 * HSL로 뽑되 **명도 0.55~0.80, 채도 0.35~0.75**로 잘라 그 사고를 막는다.
 * 색상(H)만 360도를 전부 쓰므로 개체 구분은 색상이 담당한다.
 *
 * 세 성분을 서로 다른 seed의 해시로 뽑는 이유는, 한 해시의 비트를 나눠 쓰면
 * 색상과 명도가 같이 움직여 특정 색상대만 어두워지기 때문이다.
 */
export function mobTint(name: string | null | undefined): number {
  const key = name ?? '';
  const hue = hash32(key, 0x811c9dc5) % 360;
  const sat = 0.35 + (hash32(key, 0x9e3779b9) % 41) / 100;   // 0.35 ~ 0.75
  const light = 0.55 + (hash32(key, 0x85ebca6b) % 26) / 100; // 0.55 ~ 0.80
  return hslToRgb(hue, sat, light);
}

// ============================================================
// 텍스처 로딩
// ============================================================

/** frame을 0 ~ MOB_FRAME_COUNT-1 로 자른다. */
function clampFrame(frame: number): number {
  if (!Number.isFinite(frame)) return 0;
  return Math.max(0, Math.min(MOB_FRAME_COUNT - 1, Math.floor(frame)));
}

/**
 * 몬스터 스프라이트의 Phaser 텍스처 키.
 * 같은 (계열, 방향, 프레임) 조합이면 항상 같은 키를 반환한다.
 * 개체 색은 텍스처가 아니라 setTint로 입히므로 키에 들어가지 않는다.
 */
export function mobTextureKey(family: MobFamily, dir: Direction, frame: number): string {
  return `mob|${family}|${dir}|${clampFrame(frame)}`;
}

/** 몬스터 SVG의 URL 경로. */
export function getMobSpritePath(family: MobFamily, dir: Direction, frame: number): string {
  return `/assets/gfx/mobs/${family}_${dir}_${clampFrame(frame)}.svg`;
}

// ============================================================
// 실제 몹 그림 (scripts/fetch_mob_images.py 산출물)
// ============================================================

/**
 * 이 gfxId에 실제 몹 그림이 있으면 그 규격을 돌려준다.
 *
 * 있으면 계열 SVG 대신 이 그림을 쓴다 — 계열 그림은 "사람형/짐승형" 정도만
 * 알려주지만 이쪽은 그 몹 자체다. 없으면 null이고 예전처럼 계열 SVG로 그린다.
 */
export function mobImageFor(gfxId: number): MobImageMeta | null {
  return mobImageMetaFor(gfxId);
}

/**
 * 실사 몹 그림의 Phaser 텍스처 키.
 *
 * 계열 SVG와 달리 **방향과 프레임이 키에 들어가지 않는다** — 스프라이트시트 한 장에
 * 프레임이 모두 들어 있고, 방향은 애초에 한 방향뿐이라 프레임 인덱스로만 고른다.
 */
export function mobImageTextureKey(slug: string): string {
  return `mobimg|${slug}`;
}

/** 실사 몹 그림(스프라이트시트 WebP)의 URL 경로. */
export function getMobImagePath(slug: string): string {
  return `/assets/gfx/mobs-real/${slug}.webp`;
}

/**
 * 실사 몹 그림을 Phaser 텍스처로 등록한다.
 *
 * 실패는 삼키지 않는다 — 호출자가 계열 SVG로 되돌릴 수 있어야 한다.
 *
 * @returns 등록된 텍스처 키.
 */
export function loadMobImage(scene: Phaser.Scene, meta: MobImageMeta): Promise<string> {
  return loadSpriteSheetTexture(
    scene,
    mobImageTextureKey(meta.slug),
    getMobImagePath(meta.slug),
    meta.frameWidth,
    meta.frameHeight,
  );
}

/**
 * 걷기 프레임(0=idle, 1~4)을 실사 그림의 프레임 번호로 옮긴다.
 *
 * 원본 GIF의 프레임 수는 몹마다 다르다(9장인 것도, 2장인 것도 있다). 계열 SVG의
 * 5프레임 규약을 그대로 나머지 연산으로 접으면 프레임이 적은 몹이 제자리에서
 * 떠는 것처럼 보이므로, **가진 프레임을 순서대로 도는 것**으로 본다.
 */
export function mobImageFrameIndex(meta: MobImageMeta, frame: number): number {
  if (meta.frames <= 1) return 0;
  const f = Number.isFinite(frame) ? Math.max(0, Math.floor(frame)) : 0;
  return f % meta.frames;
}

/**
 * 몬스터 스프라이트 SVG를 비동기로 로드하고 Phaser 텍스처로 등록한다.
 *
 * 실패는 삼키지 않고 그대로 reject한다 — 호출자(GameScene)가 텍스트 폴백을
 * 띄울 수 있어야 하기 때문이다. 로그도 여기서 찍지 않는다. 몹은 화면에
 * 수십 마리가 동시에 뜨므로 여기서 경고를 찍으면 콘솔이 도배된다.
 *
 * @returns 등록된 텍스처 키.
 */
export function loadMobSprite(
  scene: Phaser.Scene,
  family: MobFamily,
  dir: Direction,
  frame: number,
): Promise<string> {
  const key = mobTextureKey(family, dir, frame);
  const url = getMobSpritePath(family, dir, frame);
  return loadSvgAsTexture(scene, key, url);
}

/**
 * 한 계열이 쓰게 될 스프라이트(8방향 × 전 프레임 = 40장)를 미리 받아둔다.
 *
 * 개별 실패는 삼킨다 — 프리로드는 최적화일 뿐이고, 못 받은 것은 필요할 때
 * loadMobSprite()가 다시 요청한다. 호출자가 await하지 않아도 된다.
 *
 * @returns 실제로 준비된 텍스처 수.
 */
export async function preloadMobFamily(
  scene: Phaser.Scene,
  family: MobFamily,
): Promise<number> {
  const tasks: Promise<string | null>[] = [];

  for (const dir of DIRECTIONS) {
    for (let frame = 0; frame < MOB_FRAME_COUNT; frame++) {
      tasks.push(loadMobSprite(scene, family, dir, frame).catch(() => null));
    }
  }

  const results = await Promise.all(tasks);
  return results.filter((key) => key !== null).length;
}
